import React from "react";
import { InlineMath } from "react-katex";
import { makeSimpleClosedTask, normalizeMay2026Variant } from "../../../../matura/MaturaMay2026ClosedTaskSupport";
import { getCoordinateTriangle, pointLatex } from "./MaturaMay2026CoordinateTriangleData";
import { sideLengthsAligned, wrapNegative } from "./coordinateExplanationLatex";

function sideSquared([x1, y1], [x2, y2]) {
  return (x2 - x1) ** 2 + (y2 - y1) ** 2;
}

function sqrtLatex(value) {
  const root = Math.sqrt(value);
  if (Number.isInteger(root)) return String(root);
  for (let factor = Math.floor(root); factor >= 2; factor -= 1) {
    if (value % (factor ** 2) === 0) {
      const remainder = value / (factor ** 2);
      return remainder === 1 ? String(factor) : `${factor}\\sqrt{${remainder}}`;
    }
  }
  return `\\sqrt{${value}}`;
}

export function sideLengthsLatex(sides) {
  return sideLengthsAligned(sides);
}

function midpoint(first, second) {
  return [(first[0] + second[0]) / 2, (first[1] + second[1]) / 2];
}

function buildRandomWrongAnswers(data, correct) {
  const centroid = [
    (data.A[0] + data.B[0] + data.C[0]) / 3,
    (data.A[1] + data.B[1] + data.C[1]) / 3,
  ];
  const candidates = [
    centroid,
    [data.center[1], centroid[1]],
    [centroid[1], centroid[0]],
    midpoint(data.A, data.B),
    midpoint(data.A, data.C),
    midpoint(data.B, data.C),
  ].map(pointLatex);

  return [...new Set(candidates)].filter((candidate) => candidate !== correct).slice(0, 3);
}

export function generateTask(options = {}) {
  const variant = normalizeMay2026Variant(options);
  const data = getCoordinateTriangle(variant === "original");
  const answer = pointLatex(data.center);
  const sides = [
    { name: "AB", from: data.A, to: data.B, squared: sideSquared(data.A, data.B) },
    { name: "AC", from: data.A, to: data.C, squared: sideSquared(data.A, data.C) },
    { name: "BC", from: data.B, to: data.C, squared: sideSquared(data.B, data.C) },
  ];
  const hypotenuse = sides.reduce((longest, side) => side.squared > longest.squared ? side : longest);
  const lengthsLatex = sideLengthsLatex(sides);
  const [x1, y1] = hypotenuse.from;
  const [x2, y2] = hypotenuse.to;
  const originalOptions = [
    ["A", "(\\frac23,0)"],
    ["B", "(-\\frac12,0)"],
    ["C", "(0,\\frac23)"],
    ["D", "(0,-\\frac12)"],
  ];
  return makeSimpleClosedTask({
    type: "MaturaRightTriangleCircumcenterCoordinatesClosedTask",
    taskNumber: "24.2",
    variant,
    prompt: <span>Punkty <InlineMath math={`A=${pointLatex(data.A)},B=${pointLatex(data.B)},C=${pointLatex(data.C)}`}/> są wierzchołkami trójkąta prostokątnego. Środek okręgu opisanego na tym trójkącie ma współrzędne:</span>,
    correct: answer,
    wrong: buildRandomWrongAnswers(data, answer),
    fixedOptions: variant === "original" ? originalOptions : null,
    tags: ["geometria analityczna", "okrag opisany"],
    explanationSteps: [
      {
        title: "Obliczamy długości wszystkich boków ze wzoru na odległość punktów.",
        latex: lengthsLatex,
      },
      {
        title: <>Najdłuższym bokiem jest <InlineMath math={hypotenuse.name} />, więc to przeciwprostokątna trójkąta.</>,
        latex: `|${hypotenuse.name}|=${sqrtLatex(hypotenuse.squared)}`,
      },
      {
        title: "W trójkącie prostokątnym środek okręgu opisanego jest środkiem przeciwprostokątnej. Obliczamy więc współrzędne środka tego odcinka.",
        latex: `\\begin{aligned}S&=\\left(\\frac{x_1+x_2}{2},\\frac{y_1+y_2}{2}\\right)=\\\\&=\\left(\\frac{${wrapNegative(x1)}+${wrapNegative(x2)}}{2},\\frac{${wrapNegative(y1)}+${wrapNegative(y2)}}{2}\\right)=\\\\&=${answer}\\end{aligned}`,
      },
    ],
  });
}

export { checkClosedAnswer as checkAnswer, renderClosedAnswer as renderAnswer, renderClosedExplanation as renderExplanation, renderClosedInput as renderInput, renderClosedTask as renderTask, renderClosedUserAnswer as renderUserAnswer } from "../../../../matura/MaturaMay2026ClosedTaskSupport";
